import type { Ref } from 'vue'
import { debounceFilter, pausableWatch } from '@vueuse/core'
import * as DB from 'idb-keyval'
import { createLocalStorage, createSessionStorage } from '@/utils/cache'
import { isEmpty } from '@/utils/is'

export interface UseStorageOptions {
  /** 是否覆盖值 */
  overWrite: boolean
}

/** 设置 storage */
export const useStorage = {
  ls: createLocalStorage(),
  ss: createSessionStorage(),
  db: DB,
}

/** 双向绑定 local storage */
export function getLocalStorage<T extends (string | number | boolean | object | null)>(key: string, defaultData?: any, options?: UseStorageOptions): Ref<T> {
  return getStorage(key, 'ls', defaultData, options)
}

/** 双向绑定 session storage */
export function getSessionStorage<T extends (string | number | boolean | object | null)>(key: string, defaultData?: any, options?: UseStorageOptions): Ref<T> {
  return getStorage(key, 'ss', defaultData, options)
}

export function getStorage<T extends (string | number | boolean | object | null)>(key: string, type = 'ls', defaultData?: any, options?: UseStorageOptions): Ref<T> {
  const { overWrite } = options || {}

  const hasvalue = defaultData || defaultData === 0
  const data = ref(defaultData)
  const { pause: pauseWatch, resume: resumeWatch } = pausableWatch(
    data,
    () => {
      useStorage[type].set(key, data.value)
    },
    { flush: 'pre', deep: true, eventFilter: debounceFilter(300) },
  )

  update()

  return data

  function update() {
    pauseWatch()
    try {
      const value = useStorage[type].get(key)
      if ((hasvalue && isEmpty(value)) || (hasvalue && overWrite))
        useStorage[type].set(key, defaultData)

      data.value = useStorage[type].get(key)
    }
    catch (e) {
    }
    finally {
      resumeWatch()
    }
  }
}
